import { BrandWordmarkSvg } from '@/assets/logos/icon-wordmark-svg';
import { useWhiteLabelSafe } from '@/context/white-label';
import { useIsAgentHomeV1Enabled, useIsRibbonSidebarEnabled } from '@/features/flags';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { SIDEBAR_HEADER_ICON_SIZE, useSidebar } from '@heidi/design-system/page-shell';
import { PanelToggleButton } from '@heidi/design-system/panel-toggle-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { useTranslations } from '@heidi/runtime/i18n';
import type { MouseEvent } from 'react';
import { useIntl } from 'react-intl';

/**
 * Heidi logo component for sidebar header.
 * Shows logo icon with animated "Heidi" text that fades on collapse.
 * Legacy (flag-off) collapsed state exposes a pinned-visible toggle button over the logo to expand the sidebar.
 * Nav-v3 (isAgentHomeV1Enabled) has no hover affordance on the logo. Its icon
 * sidebar expands through PageShell's rail/background or the sidebar hotkey;
 * label-icons mode is an intentionally fixed ribbon that cannot expand.
 * When expanded (desktop only), a collapse toggle button is shown to the right of the logo.
 */
export function HeidiLogo() {
  const intl = useIntl();
  const { t } = useTranslations();
  const { state, toggleSidebar, collapsible } = useSidebar();
  const isMobile = useIsMobile();
  const { isWhiteLabel } = useWhiteLabelSafe();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  const isCollapsed = state === 'collapsed';
  const isExpanded = state === 'expanded';
  // Nav-v3 in icon mode still needs the expanded collapse button. In
  // label-icons mode SidebarProvider forces the ribbon collapsed, so this
  // wrapper stays hidden even though Agent Home keeps showToggles true.
  const showToggles = !isMobile && (!isRibbonSidebarEnabled || isAgentHomeV1Enabled);
  const collapseSidebarLabel = intl.formatMessage({
    defaultMessage: 'Collapse sidebar',
    id: 'nu8okET3CQ',
    description: 'Accessible label for collapsing the primary navigation',
  });

  // In label-icons mode with new sidebar enabled, show only the logo at 34x34
  const isLabelIconsMode = isRibbonSidebarEnabled && collapsible === 'label-icons';

  // In white-label mode, show only the logo (no wordmark) at a larger size
  const showWordmark = !isWhiteLabel && !isLabelIconsMode;
  // Nav-v3 uses the icon as the collapsed rail's resting state, but the
  // expanded header is the wordmark alone. White-label customers still need
  // their supplied logo in both states because there is no Heidi wordmark.
  const showBrandIcon = !isAgentHomeV1Enabled || !isExpanded || isWhiteLabel;

  const handleToggleClick = (event: MouseEvent<HTMLButtonElement>) => {
    toggleSidebar();
    // Blur pointer clicks so tab-return doesn't re-fire Radix's focus tooltip;
    // detail === 0 (keyboard) keeps focus and its ring.
    if (event.detail > 0) event.currentTarget.blur();
  };

  // Only the legacy (flag-off) toggles need a Tooltip.
  const withLegacyTooltip = (control: JSX.Element, label: string) =>
    isAgentHomeV1Enabled ? (
      control
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>{control}</TooltipTrigger>
        <TooltipContent side="right" align="center" className="px-2 py-1 text-sm">
          {label}
        </TooltipContent>
      </Tooltip>
    );

  // Nav-v3 has no expand affordance on the logo itself; its expandable icon
  // sidebar uses PageShell's rail/background or the sidebar hotkey. This
  // overlay only exists for the legacy flag-off collapsed rail.
  const expandControl = isAgentHomeV1Enabled
    ? null
    : withLegacyTooltip(
        <PanelToggleButton
          open={isExpanded}
          side="left"
          variant="ghost"
          size="sm"
          onClick={handleToggleClick}
          aria-label={t('sidenav.expandSidebar')}
          tabIndex={isCollapsed ? 0 : -1}
          className={cn(
            // size-full overrides the DS Button's box so the overlay fills the logo; hover
            // stays background-secondary (not ghost's tertiary) to match the chrome; ring-inset
            // stops the outset DS focusRing being clipped to nothing by the overflow-hidden box.
            'pointer-events-none absolute inset-0 z-10 size-full text-text-primary opacity-0 transition-opacity duration-100 ease-in-out hover:bg-background-secondary-hover focus-visible:ring-inset active:bg-background-secondary-pressed motion-reduce:transition-none',
            'cursor-e-resize group-data-[collapsible=icon]:pointer-events-auto group-data-[collapsible=icon]:opacity-100'
          )}
        />,
        t('sidenav.expandSidebar')
      );

  const collapseControl = withLegacyTooltip(
    <PanelToggleButton
      open={isExpanded}
      side="left"
      variant="ghost"
      size="sm"
      onClick={handleToggleClick}
      aria-label={
        isAgentHomeV1Enabled ? collapseSidebarLabel : t('sidenav.collapseSidebar')
      }
      tabIndex={isExpanded ? 0 : -1}
      className={cn(
        // ring-inset because the outset DS focusRing is clipped to nothing by the overflow-hidden box.
        'text-text-secondary hover:bg-background-secondary-hover focus-visible:ring-inset active:bg-background-secondary-pressed motion-reduce:transition-none',
        !isAgentHomeV1Enabled && 'cursor-w-resize'
      )}
    />,
    t('sidenav.collapseSidebar')
  );

  return (
    <div
      className={cn('flex items-center justify-between', isLabelIconsMode && 'w-full')}
    >
      <div
        className={cn(
          'flex items-center gap-1 overflow-hidden transition-transform duration-200 ease-out motion-reduce:transition-none',
          // Center for label-icons mode
          isLabelIconsMode && 'w-full justify-center py-px'
        )}
      >
        {/* Logo container with legacy-only expand button when collapsed (desktop only) */}
        {showBrandIcon && (
          <div
            className={cn(
              'relative flex items-center justify-center',
              !isWhiteLabel && 'shrink-0'
            )}
            style={{
              // White-label logos may be wide (wordmarks), so allow auto width
              width: isWhiteLabel
                ? 'auto'
                : isLabelIconsMode
                  ? '2.125rem'
                  : SIDEBAR_HEADER_ICON_SIZE,
              height: isLabelIconsMode ? '2.125rem' : SIDEBAR_HEADER_ICON_SIZE,
              maxWidth: isWhiteLabel ? '140px' : undefined,
            }}
          >
            {/* Legacy-only expand overlay, pinned visible over the collapsed logo. */}
            {showToggles && expandControl}
            <HeidiLogoMark
              aria-label="Heidi"
              artwork="standard"
              className={cn(
                'pointer-events-none transition-opacity duration-200 ease-out motion-reduce:transition-none',
                isLabelIconsMode ? 'size-[2.125rem]' : 'size-7',
                !isMobile &&
                  (isAgentHomeV1Enabled
                    ? 'opacity-100'
                    : 'group-data-[collapsible=icon]:opacity-0')
              )}
            />
          </div>
        )}
        {/* Wordmark - hidden in label-icons mode, white-label mode, and collapsed states */}
        {showWordmark && (
          <div
            className={cn(
              'relative flex shrink-0 items-center justify-center transition-[opacity,transform] duration-200 ease-out group-data-[collapsible=icon]:-translate-x-0.5 group-data-[collapsible=label-icons]:-translate-x-0.5 group-data-[collapsible=icon]:opacity-0 group-data-[collapsible=label-icons]:opacity-0 motion-reduce:transform-none motion-reduce:transition-none',
              // Expanded nav-v3 items start their icon glyphs 20px from the
              // sidebar edge (group p-2 + item px-3). Align the wordmark to
              // that glyph column, rather than to the 8px active-state edge.
              isAgentHomeV1Enabled && isExpanded && 'ms-3'
            )}
            style={{ height: SIDEBAR_HEADER_ICON_SIZE }}
          >
            {/* Wordmark - hidden on collapsed state. Negative margin aligns with Figma's 8.14% top / 16.86% bottom inset ratio */}
            <BrandWordmarkSvg
              alt=""
              height={21}
              className="pointer-events-none mt-[-1.5px]"
              priority
            />
          </div>
        )}
      </div>

      {/* Collapse toggle button - only visible on desktop when sidebar is expanded */}
      {showToggles && (
        <div
          className={cn(
            'overflow-hidden transition-[width,opacity] duration-200 ease-out motion-reduce:transition-none',
            isExpanded ? 'w-8 opacity-100' : 'w-0 opacity-0'
          )}
        >
          {collapseControl}
        </div>
      )}
    </div>
  );
}
